<script setup lang="ts">
import { AxiosError } from 'axios'
import BenefitReward from '@assets/images/image-coin.svg?url'
import BenefitRent from '@assets/images/image-rent.svg?url'
import BenefitConvenience from '@assets/images/image-house.svg?url'
import BenefitSurvival from '@assets/images/image-survival.svg?url'
import type { Radio } from '@components/NovaRadio/NovaRadio.types'
import type { Theme } from '@components/NovaButtonText/NovaButtonText.types'
import type { FacilityCode } from '@store'
import { ROUTES } from '@configs'
import { FacilityRentalAvailableStatus, FacilityRentalStatus } from '@store'

interface ApplyRentAction {
  [key: string]: any
  label: string
  theme: Theme
  loading: boolean
  action: () => void
}
interface ApplyRentConditionOptional {
  value: FacilityCode
  conditions: Radio<FacilityCode>[]
  changeCondition: (value: FacilityCode) => void
}

const layoutStore = useLayoutStore()
const userStore = useUserStore()
const { isSignIn } = storeToRefs(userStore)
const facilityStore = useFacilityStore()
const { rentCheckConditions, rentAvailableStatus } = storeToRefs(facilityStore)
const config = useRuntimeConfig()

definePageMeta({
  layout: 'one-column',
  middleware: ['apply-rent-guard'],
})

useSetSEO()

await useAsyncData('rentCheckConditions', async () => {
  try {
    await facilityStore.fetchFacilityRentalCheckConditions()
  } catch (err) {
    if (err instanceof AxiosError) {
      throw showError(err)
    }
  }
})

// model: 다양한 혜택
const benefits = [
  {
    label: '개인 연습실 무료 입주',
    description:
      '개인 연습실 신청자는 개인 연습실을 <strong>3개월</strong> 동안 무료로 사용하실 수 있습니다.',
    image: BenefitRent,
    imageAlt: 'The Moon Ent. Rent',
  },
  {
    label: '편의 시설 사용',
    description:
      '입주 기간 동안 <strong>악기</strong>와 <strong>휴게실</strong>을 자유롭게 사용하실 수 있으며, <strong>안무 연습실</strong>, <strong>합주실</strong>, <strong>스튜디오 등을 예약</strong>하여 사용할 수 있습니다.',
    image: BenefitConvenience,
    imageAlt: 'The Moon Ent. Convenience',
  },
  {
    label: '서바이벌 참여 기회',
    description:
      '<strong>The Moon Ent.</strong>에서 하반기에 주최하는 서바이벌 프로그램에 서류 심사 없이 바로 참여할 수 있는 기회가 주어집니다.',
    image: BenefitSurvival,
    imageAlt: 'The Moon Ent. Survival',
  },
  {
    label: '매주 활동 리워드 지급',
    description: `<strong>${config.public.APP_NAME}</strong> 활동 시 매주 리워드 지급`,
    image: BenefitReward,
    imageAlt: 'The Moon Ent. Reward',
  },
]

// model: 신청 시설
// 모델: 전환 신청 요건
const conditions: Radio[] = rentCheckConditions.value
  ? rentCheckConditions.value.map((item) => ({
      value: item.fcltrntFcltsTyCode,
      label: item.fcltrntFcltsTyCodeNm,
      direction: 'row',
    }))
  : []

const conditionOptional = reactive<ApplyRentConditionOptional>({
  value: '',
  conditions,
  changeCondition: (value: FacilityCode) => {
    conditionOptional.value = value
  },
})

// model: 유의 사항
const notices = [
  'The Moon Ent. 입주 신청 심사(서류심사) 결과는 영업일 기준 20일 이내에 개별 안내 드립니다.',
  '신청일 기준 60일 이후부터 재신청이 가능합니다.',
  '전체 심사 과정: 서류 심사 - 면접 - 입주자/대기 입주자 선발',
  '위의 입주 신청 요건과 혜택은 I LIKE LM과 The Moon Ent. 운영 상황에 따라 변동 및 중단될 수 있습니다.',
  '문의: <a href="mailto:contact@themoonent.com">contact@themoonent.com</a>',
]

// model: 입주신청서 작성하기 버튼
const applyAction = reactive<ApplyRentAction>({
  label:
    rentAvailableStatus.value ===
    FacilityRentalAvailableStatus.UNAVAILABLE_APPLIED_LESS_THAN_20_DAYS
      ? '완료 페이지로 가기'
      : '입주신청서 작성하기',
  theme:
    rentAvailableStatus.value ===
    FacilityRentalAvailableStatus.UNAVAILABLE_APPLIED_LESS_THAN_20_DAYS
      ? 'secondary-green-light-80'
      : 'primary-blue',
  loading: false,
  action: () =>
    useFacilityNextStep({
      onStart: () => {
        applyAction.loading = true
      },
      onError: (err: any) => {
        console.log(err)
      },
      onEnd: () => {
        applyAction.loading = false
      },
      onNextStep: () => {
        useNavigations({
          url: useRoutePathIdChange(ROUTES.APPLY_RENT_FACILITY.path, {
            id: conditionOptional.value,
          }),
        })
      },
      facilityCode: conditionOptional.value,
    }),
})

onMounted(() => {
  layoutStore.updateMainBgWhite(true)
  facilityStore.fetchFacilityRentalCheckConditions()
  if (
    facilityStore.rentalHistory &&
    facilityStore.rentalHistory.applicationUnavailableCode ===
      FacilityRentalStatus.LESS_THAN_60
  ) {
    conditionOptional.changeCondition(
      facilityStore.rentalHistory.fcltrntFcltsTyCode
    )
  } else {
    conditionOptional.changeCondition('P1')
  }
})

onBeforeUnmount(() => {
  layoutStore.updateMainBgWhite(false)
})
</script>

<template>
  <div class="page-apply-rent">
    <div class="inner">
      <!-- 헤더 -->
      <div class="header">
        <div class="header-group">
          <ClientOnly>
            <p v-if="isSignIn" class="nickname">
              {{ userStore.user!.userNcnm }}님,
            </p>
          </ClientOnly>
          <h4 class="title">The Moon Ent.와 함께하세요.</h4>
          <p class="description mg-t-3u">
            꿈 꾸는 젊은이들의 놀이터, <strong>The Moon Ent.</strong>는<br />
            크리에이터 팬덤 커뮤니티
            <strong>{{ config.public.APP_NAME }}</strong
            >와 함께합니다.<br /><br />
            <strong>{{ config.public.APP_NAME }}</strong
            >에서 <strong>The Moon Ent.</strong>의 아티스트들은 창작 콘텐츠를
            업로드하고,<br />
            팬들과 소통하며 자유롭게 팬덤을 형성할 수있습니다.
          </p>
        </div>
      </div>

      <!-- 다양한 혜택 -->
      <section class="section benefits">
        <h4 class="label">다양한 혜택</h4>
        <ul class="benefit-list">
          <li v-for="(benefit, index) in benefits" :key="index" class="benefit">
            <div class="image-wrap">
              <img :src="benefit.image" :alt="benefit.imageAlt" />
            </div>

            <div class="info-wrap">
              <p class="label">{{ benefit.label }}</p>
              <p v-dompurify-html="benefit.description" class="description" />
            </div>
          </li>
        </ul>
      </section>

      <!-- 필수 조건(KYC 인증) -->
      <section class="section conditions">
        <h4 class="label">필수 조건</h4>
        <ClientOnly>
          <div class="condition-list">
            <NovaCheckbox
              :label="'KYC 인증'"
              :is-check="userStore.user?.kycAt === 'Y'"
              :theme="'circle'"
              :readonly="true"
            />
          </div>
        </ClientOnly>
      </section>

      <!-- 시설 선택 -->
      <section class="section conditions">
        <h4 class="label">사용하실 시설을 선택해주세요.</h4>
        <div :class="['condition-list']">
          <NovaRadioGroup
            :current="conditionOptional.value"
            :radios="conditionOptional.conditions"
            :direction="'vertical'"
            :equal-width="true"
            @on-change-radio="conditionOptional.changeCondition"
          />
        </div>
        <!-- 연습실 보러가기 링크 -->
        <p class="link">
          <a href="https://themoonent.com/space/intro" target="_blank">
            ▶ 연습실 보러가기
          </a>
        </p>
      </section>

      <!-- 유의사항 -->
      <section class="section notices">
        <h4 class="label">유의 사항</h4>
        <NovaDescriptions :descriptions="notices" />
      </section>

      <!-- 입주 신청 버튼-->
      <ClientOnly>
        <section class="section apply">
          <NovaButtonText
            :label="applyAction.label"
            :theme="applyAction.theme"
            :loading="applyAction.loading"
            :full-width="false"
            @click="applyAction.action"
          />
        </section>
      </ClientOnly>
    </div>
  </div>
</template>

<style scoped lang="scss">
.page-apply-rent {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  > .inner {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 632px + 24px + 24px;
    padding: 15px 24px 48px;

    .header {
      display: flex;
      flex-direction: column;
      gap: 16px;

      .header-group {
        display: flex;
        flex-direction: column;
        gap: 10px;

        > .nickname {
          @include text-style($text-display-bold);
          line-height: 18px;
        }

        > .title {
          @include text-style($text-custom-24-bold);
        }

        > .description {
          @include text-style($text-body-14-regular);
          line-height: 16px;
        }
      }

      > .benefit {
        @include text-style($text-body-12-medium);
        line-height: 14px;
        color: $color-text-8;
      }
    }
  }

  .section {
    display: flex;
    flex-direction: column;
    gap: 15px;

    // 다양한 혜택
    &.benefits {
      margin-top: 50px;

      > .benefit-list {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        -webkit-grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 48px;
        row-gap: 30px;
        padding: 15px;
        background-color: $color-neutral-light-5;
        border-radius: $radius-10;

        @include mobile {
          grid-template-columns: repeat(1, minmax(0, 1fr));
          -webkit-grid-template-columns: repeat(1, minmax(0, 1fr));
          column-gap: 0;
        }

        .benefit {
          display: flex;
          align-items: center;
          gap: 15px;

          .image-wrap {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 50px;
            height: 50px;

            > img {
              width: 50px;
              height: auto;
            }
          }

          .info-wrap {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            gap: 10px;

            > .label {
              @include text-style($text-body-12-medium);
              line-height: 15px;
              color: $color-black;
            }

            > .description {
              @include text-style($text-body-12-regular);
              line-height: 16px;
              color: $color-text-2;
            }
          }
        }
      }
    }

    // 신청 요건
    &.conditions {
      margin-top: 40px;

      > .condition-list {
        display: flex;
        flex-direction: column;
        gap: 16px;

        &.disabled {
          pointer-events: none;
        }

        .condition-link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          cursor: pointer;
        }
      }
      .link {
        color: #5b89ff;
        :hover {
          text-decoration: underline;
        }
      }
    }

    // 유의 사항
    &.notices {
      margin-top: 40px;
    }

    // 신청 버튼
    &.apply {
      margin-top: 65px;
      align-items: center;
    }

    > .label {
      @include text-style($text-body-14-bold);
      line-height: 16px;
      color: $color-text-1;
    }
  }
}
</style>
